'use client'

import Link from 'next/link'
import { Section, SectionHeading } from '@/components/platform/v2/Section'
import { ProductHero, Statement, FlowStrip } from '@/components/platform/v2/ProductKit'
import { PageCta } from '@/components/platform/v2/PageEdge'
import { ArrowRight, ArrowDown } from 'lucide-react'

const SCATTER = ['CRM', 'Финансы', 'Контент', 'Юридические задачи', 'Закупки', 'Клиенты']

const ECOSYSTEM = [
    {
        name: 'VCENTRE.ONE',
        text: 'Готовая система управления и стандартное приложение с BASE; расширенные каналы, расширения и бизнес-AI по мере роста.',
        accent: true,
    },
    { name: 'VCENTRE.FINANCE', text: 'Скоро. Профессиональная платформа управления финансами владельца и команды.', accent: false },
    { name: 'VCENTRE.VOTE', text: 'Доступно. Самостоятельная платформа премий, конкурсов и голосований.', accent: false },
    { name: 'VCENTRE.CONTENT', text: 'Скоро. Создание контента с AI: бесплатная база и единый AI-баланс.', accent: false },
    { name: 'VCENTRE.JURIST', text: 'Скоро. AI-инструменты для юридических документов: бесплатная база и единый AI-баланс.', accent: false },
]

const EXTRA = ['VCENTRE.TOOLS', 'VCENTRE.ICONS']

const AI_INPUTS = ['Данные бизнеса', 'Процессы', 'Контекст', 'AI']
const AI_OUTPUTS = ['Рекомендации', 'Аналитика', 'Действия', 'Помощь команде']

const CompanyPageComponent = () => {
    return (
        <div className="vcentre-public pt-24 sm:pt-28 md:pt-32">
            {/* 1. HERO */}
            <ProductHero
                eyebrow="VCENTRE.IT"
                titleLines={['Мы — бизнес,', 'который создаёт', 'технологии для бизнеса.']}
                accentLine={2}
                lead="Мы сами прошли продажи, клиентов, поставщиков, сотрудников, документы, финансы, продвижение и ежедневную операционку. Поэтому создаём продукты не из представления о том, как бизнес должен работать, а из понимания того, как он работает на самом деле."
                primary={{ label: 'Посмотреть экосистему', to: '#ecosystem' }}
                secondary={{ label: 'VCENTRE.ONE', to: '/platform' }}
                visual={
                    <img
                        src={'/assets/images/platform/company-mascot-hero.png'}
                        alt="Маскот VCENTRE.IT — утёнок в чёрном худи с фразой «Мы — бизнес, который создаёт технологии для бизнеса»"
                        width={1122}
                        height={1402}
                        loading="eager"
                        className="w-full max-w-[330px] animate-float drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)] sm:max-w-[420px]"
                    />
                }
            />

            {/* 2. НЕ ЕЩЁ ОДИН СЕРВИС */}
            <Section tone="quiet" id="ecosystem">
                <SectionHeading
                    eyebrow="Не ещё один сервис"
                    title="Не ещё один сервис. Одна экосистема."
                    text="Сегодня предприниматель работает сразу в десятках систем: CRM, мессенджерах, банках, таблицах, облаках, сервисах контента, маркетинга, документов и AI. Каждая решает свою задачу. Но вместе они редко понимают один бизнес. Мы строим другую модель."
                />

                <div className="mt-10 grid gap-4 lg:grid-cols-[1fr_auto_1fr] lg:items-center lg:gap-6">
                    {/* разрозненные системы */}
                    <div className="rounded-[1.5rem] border border-border/60 bg-[hsl(var(--deep-black))] p-5">
                        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Разрозненные системы</p>
                        <div className="mt-4 flex flex-col gap-2">
                            {SCATTER.map((s) => (
                                <div
                                    key={s}
                                    className="flex items-center justify-between gap-3 rounded-xl border border-border/50 bg-[hsl(var(--charcoal))] px-3.5 py-2.5"
                                >
                                    <span className="text-sm font-semibold text-foreground/80">{s}</span>
                                    <span className="text-muted-foreground/50">×</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* стрелка */}
                    <div className="flex items-center justify-center py-2">
                        <ArrowRight className="hidden h-6 w-6 text-primary lg:block" />
                        <ArrowDown className="mx-auto h-6 w-6 text-primary lg:hidden" />
                    </div>

                    {/* центр */}
                    <div className="flex h-full flex-col items-center justify-center gap-3 rounded-[1.5rem] border border-primary/40 bg-primary/10 px-6 py-8 text-center">
                        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary/80">Единый контур</span>
                        <span className="text-xl font-extrabold tracking-[-0.02em] text-primary sm:text-2xl">VCENTRE</span>
                        <span className="text-xs text-foreground/70">Семейство платформ, которые объединяются в один бизнес</span>
                    </div>
                </div>

                <div className="mt-8">
                    <Statement note="Каждая система решает свою задачу. Вместе они формируют систему.">
                        VCENTRE — семейство самостоятельных платформ, которые могут работать отдельно, но объединяются в единый цифровой
                        контур бизнеса.
                    </Statement>
                </div>
            </Section>

            {/* 3. НАША ЭКОСИСТЕМА */}
            <Section>
                <SectionHeading
                    eyebrow="Наша экосистема"
                    title="VCENTRE.ONE — центр экосистемы"
                    text="Расширения добавляют механики внутри ONE. Самостоятельные платформы решают специализированные задачи. Магазин даёт общие ресурсы на уровне аккаунта."
                />
                <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {ECOSYSTEM.map((p) => (
                        <article
                            key={p.name}
                            className={
                                p.accent
                                    ? 'surface flex flex-col items-center gap-3 text-center border-primary/40 bg-primary/[0.07]'
                                    : 'surface surface-hover flex flex-col items-center gap-3 text-center'
                            }
                        >
                            <h3
                                className={
                                    p.accent
                                        ? 'text-lg font-extrabold text-primary sm:text-xl'
                                        : 'text-lg font-bold text-foreground sm:text-xl'
                                }
                            >
                                {p.name}
                            </h3>
                            <p className="body-text">{p.text}</p>
                        </article>
                    ))}
                </div>
                <div className="mt-6 flex flex-wrap items-center gap-3 rounded-2xl border border-border/50 bg-[hsl(var(--deep-black))] px-5 py-4">
                    <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Дополнительные продукты</span>
                    <div className="flex flex-wrap gap-2">
                        {EXTRA.map((e) => (
                            <span
                                key={e}
                                className="rounded-full border border-border/50 bg-[hsl(var(--charcoal))] px-3 py-1.5 text-sm text-foreground/85"
                            >
                                {e}
                            </span>
                        ))}
                    </div>
                </div>
            </Section>

            {/* 4. AI — НЕ ОТДЕЛЬНАЯ КНОПКА */}
            <Section tone="quiet">
                <SectionHeading
                    eyebrow="Искусственный интеллект"
                    title="AI должен знать ваш бизнес."
                    text="AI-помощь с контентом и клиентский помощник доступны с BASE. GENIUS добавляет AI-контур владельца и команды: работу с данными и процессами, поиск проблем и управленческие рекомендации. Мы развиваем его, чтобы он глубже понимал контекст компании."
                />

                <div className="mt-10 grid gap-4 lg:grid-cols-[1fr_auto_1fr] lg:items-center lg:gap-6">
                    <div className="rounded-[1.5rem] border border-border/60 bg-[hsl(var(--deep-black))] p-5">
                        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Что получает AI</p>
                        <div className="mt-4 flex flex-col gap-2">
                            {AI_INPUTS.map((s) => (
                                <div
                                    key={s}
                                    className="flex items-center gap-3 rounded-xl border border-border/50 bg-[hsl(var(--charcoal))] px-3.5 py-2.5"
                                >
                                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                                    <span className="text-sm font-semibold text-foreground/90">{s}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center justify-center py-2">
                        <ArrowRight className="hidden h-6 w-6 text-primary lg:block" />
                        <ArrowDown className="mx-auto h-6 w-6 text-primary lg:hidden" />
                    </div>

                    <div className="rounded-[1.5rem] border border-primary/40 bg-primary/10 p-5">
                        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary/80">Чем помогает</p>
                        <div className="mt-4 flex flex-col gap-2">
                            {AI_OUTPUTS.map((s) => (
                                <div
                                    key={s}
                                    className="flex items-center gap-3 rounded-xl border border-primary/30 bg-[hsl(var(--charcoal))] px-3.5 py-2.5"
                                >
                                    <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                                    <span className="text-sm font-semibold text-foreground">{s}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mt-8">
                    <Statement>
                        Не заменить предпринимателя. <span className="text-primary">Освободить предпринимателя.</span>
                    </Statement>
                </div>
            </Section>

            {/* 5. КУДА МЫ ИДЁМ */}
            <Section>
                <SectionHeading
                    eyebrow="Куда мы идём"
                    title="Мы строим цифровую операционную систему бизнеса."
                    text="Наша конечная цель — единое пространство, где предприниматель видит клиентов, продажи, коммуникации, контент, финансы, документы, закупки, сотрудников, задачи и аналитику. А рядом работает AI, который понимает не отдельный запрос, а контекст всей компании."
                />

                <div className="mt-8">
                    <FlowStrip
                        steps={[
                            { title: 'Клиенты и продажи', note: 'с чего начинается бизнес' },
                            { title: 'Коммуникации и каналы', note: 'как бизнес доходит до клиента' },
                            { title: 'Контент, финансы, документы', note: 'как бизнес держится вместе' },
                            { title: 'Единая операционная система', note: 'AI понимает контекст всей компании', accent: true },
                        ]}
                    />
                </div>

                <div className="mt-8">
                    <Statement note="Чтобы предприниматель снова мог заниматься предпринимательством.">
                        Технологии должны убирать сложность, <span className="text-primary">а не создавать новую.</span>
                    </Statement>
                </div>
            </Section>

            {/* 6. ФИНАЛЬНЫЙ CTA */}
            <Section>
                <PageCta
                    eyebrow="Обсудим ваш бизнес"
                    title="Покажите, как сейчас работает ваш бизнес"
                    text={<p>Мы покажем, как связать ваш бизнес с экосистемой VCENTRE — клиентами, продажами и цифровыми каналами.</p>}
                    actions={
                        <>
                            <Link href="/signin?source=company" className="btn-primary w-full sm:w-auto">
                                Собрать своё приложение <ArrowRight className="h-4 w-4" />
                            </Link>
                            <Link href="/platform/contacts" className="btn-ghost w-full sm:w-auto">
                                Связаться с нами
                            </Link>
                        </>
                    }
                    visual={
                        <img
                            src={'/assets/images/platform/company-mascot-cta.png'}
                            alt="Маскот VCENTRE.IT — утёнок в чёрном худи с ракетой и фразой «Запускаем быстро»"
                            width={1254}
                            height={1254}
                            loading="lazy"
                            className="w-full max-w-[260px] drop-shadow-[0_24px_48px_rgba(0,0,0,0.55)]"
                        />
                    }
                />
            </Section>
        </div>
    )
}

export default CompanyPageComponent
